var Emitter = require('../../../emitter')
var Log = require('../../../log')
var YBRequest = require('../../request')

var CdnParser = Emitter.extend(
  /** @lends CdnParser.prototype */
  {
    /**
     * This class helps ResourceTransform parse CDN Nodes.
     *
     * @constructs CdnParser
     * @extends Emitter
     * @memberof youbora
     */
    constructor: function (options) {
      this._options = Object.assign({
        cdnName: null,
        parsers: [],
        requestHeaders: {},
        parseType: function () { return 0 }
      }, options)

      this._responses = {}
    },

    /**
     * Emits DONE event
     */
    done: function () {
      this.emit(CdnParser.Event.DONE)
    },

    addParser: function (parser) {
      this._options.parsers.push(parser)
      return this
    },

    setCdnName: function (name) {
      this._options.cdnName = name
      return this
    },

    setRequestHeader: function (key, value) {
      this._options.requestHeaders[key] = value
      return this
    },

    setParseType: function (parser) {
      this._options.parseType = parser
      return this
    },

    getCdnName: function () {
      return this._cdnName
    },

    getNodeHost: function () {
      return this._cdnNodeHost
    },

    getNodeTypeString: function () {
      return this._cdnNodeTypeString
    },

    getNodeType: function () {
      return this._cdnNodeType
    },

    getResponses: function () {
      return this._responses
    },

    /**
     * Parses given headers to check for matches.
     */
    parse: function (url, responses) {
      this._responses = responses || {}
      var headerString = JSON.stringify(this._options.requestHeaders)
      if (this._responses[headerString]) {
        this._parseResponse(this._responses[headerString])
      } else {
        this._requestResponse(url)
      }
    },

    _requestResponse: function (url) {
      var headerString = JSON.stringify(this._options.requestHeaders)

      var request = new YBRequest(url, null, null, {
        method: 'HEAD',
        maxRetries: 0,
        requestHeaders: this._options.requestHeaders
      })

      request.on(YBRequest.Event.SUCCESS, function (resp) {
        this._responses[headerString] = resp.getXHR().getAllResponseHeaders()
        this._parseResponse(this._responses[headerString])
      }.bind(this))

      request.on(YBRequest.Event.ERROR, function (resp) {
        this.done()
      }.bind(this))

      request.send()
    },

    _parseResponse: function (headers) {
      this._options.parsers.forEach(function (parser) {
        headers.split('\n').forEach(function (line) {
          var index = line.indexOf(':')
          if (index !== -1) {
            var key = line.slice(0, index)
            if (key === parser.headerName) {
              this._executeParser(parser, line.slice(index + 1))
            }
          }
        }.bind(this))
      }.bind(this))
      this.done()
    },

    _executeParser: function (parser, value) {
      try {
        var matches = parser.regex.exec(value.trim())
        if (matches !== null) {
          if (this._options.cdnName) this._cdnName = this._options.cdnName
          switch (parser.element) {
            case 'host':
              this._cdnNodeHost = matches[1]
              break
            case 'type':
              this._cdnNodeTypeString = matches[1]
              this._cdnNodeType = this._options.parseType(this._cdnNodeTypeString)
              break
            case 'host+type':
              this._cdnNodeHost = matches[1]
              this._cdnNodeTypeString = matches[2]
              this._cdnNodeType = this._options.parseType(this._cdnNodeTypeString)
              break
            case 'type+host':
              this._cdnNodeTypeString = matches[1]
              this._cdnNodeType = this._options.parseType(this._cdnNodeTypeString)
              this._cdnNodeHost = matches[2]
              break
            case 'name':
              this._cdnName = matches[1].toUpperCase()
              break
          }
        }
      } catch (err) {
        Log.warn('CDN parsing for ' + this._options.cdnName +
          ' could not parse header value ' + value)
      }
    }

  },

  /** @lends CdnParser */
  {
    // Static members

    /**
     * List of events that could be fired from this class.
     * @enum
     */
    Event: {
      /** Notifies that this CdnParser is done processing. */
      DONE: 'done'
    },

    // Static members
    _cdns: {
      Level3: {
        cdnName: 'LEVEL3',
        parsers: [{
          element: 'host+type',
          headerName: 'X-WR-DIAG',
          regex: /Host:(.+)\sType:(.+)/
        }],
        requestHeaders: {
          'X-WR-DIAG': 'host'
        },
        parseType: function (type) {
          switch (type) {
            case 'TCP_HIT':
            case 'TCP_MEM_HIT':
            case 'TCP_IMS_HIT':
              return 1
            case 'TCP_MISS':
              return 2
            default:
              return 0
          }
        }
      },
      Cloudfront: {
        cdnName: 'CLOUDFRT',
        parsers: [{
          element: 'host',
          headerName: 'X-Amz-Cf-Id',
          regex: /(.+)/
        }, {
          element: 'type',
          headerName: 'X-Cache',
          regex: /(\S+)\s.+/
        }],
        parseType: function (type) {
          switch (type) {
            case 'Hit':
              return 1
            case 'Miss':
              return 2
            default:
              return 0
          }
        }
      },
      Akamai: {
        cdnName: 'AKAMAI',
        parsers: [{
          element: 'type+host',
          headerName: 'X-Cache',
          regex: /(.+)\sfrom\s.+\(.+\/(.+)\).*/
        }],
        parseType: function (type) {
          switch (type) {
            case 'TCP_HIT':
              return 1
            case 'TCP_MISS':
              return 2
            default:
              return 0
          }
        }
      },
      Highwinds: {
        cdnName: 'HIGHNEGR',
        parsers: [{
          element: 'host+type',
          headerName: 'X-HW',
          regex: /.+,[0-9]+\.(.+)\.(.+)/
        }],
        parseType: function (type) {
          switch (type) {
            case 'c':
            case 'x':
              return 1
            default:
              return 2
          }
        }
      },
      Fastly: {
        cdnName: 'FASTLY',
        parsers: [{
          element: 'host',
          headerName: 'X-Served-By',
          regex: /([^,\s]+)$/
        }, {
          element: 'type',
          headerName: 'X-Cache',
          regex: /([^,\s]+)$/
        }],
        parseType: function (type) {
          switch (type) {
            case 'HIT':
              return 1
            case 'MISS':
              return 2
            default:
              return 0
          }
        }
      },
      Balancer: {
        parsers: [{
          element: 'name',
          headerName: null,
          regex: /(.+)/
        }]
      }
    },

    setBalancerHeaderName: function (name) {
      CdnParser._cdns.Balancer.parsers[0].headerName = name
    },

    create: function (key) {
      if (CdnParser._cdns[key]) {
        return new CdnParser(CdnParser._cdns[key])
      } else {
        Log.warn('Tried to create an unexisting CdnParser named ' + key)
      }
    },

    add: function (key, parser) {
      CdnParser._cdns[key] = parser
    }
  }
)

module.exports = CdnParser



//////////////////
// WEBPACK FOOTER
// ./src/comm/transform/resourceparsers/cdnparser.js
// module id = 15
// module chunks = 0